/**
 * Header behaviour: publishes --header-h and runs the menu panel.
 *
 * The header is solid and in flow, so nothing here affects legibility: with JS
 * dead the bar still reads, the Menu button does nothing, and every destination
 * is in the footer.
 */

import { setScrollLock } from './scroll-lock.js';
import { focusables, trapFocus } from './focus-trap.js';

const EXIT_MS = 240;

export default function initHeader() {
	const header = document.querySelector('[data-awdd-header]');
	if (!header) return;

	// ===== --header-h =====
	// CSS ships the static build's measured values; this keeps it exact when a
	// translated label wraps the bar. It is the bar's own height and does not
	// include the admin bar: that offset is --header-bottom, below.
	const root = document.documentElement;
	const publish = () => root.style.setProperty('--header-h', `${header.offsetHeight}px`);
	publish();
	if ('ResizeObserver' in window) new ResizeObserver(publish).observe(header);

	// ===== Menu panel =====
	const toggle = header.querySelector('[data-awdd-menu-toggle]');
	const drawer = document.getElementById('site-menu');
	const scrim = header.querySelector('[data-awdd-drawer-scrim]');
	if (!toggle || !drawer || !scrim) return;

	const closeBtn = drawer.querySelector('[data-awdd-menu-close]');
	const mobile = window.matchMedia('(max-width: 1023.98px)');
	let open = false;
	let returnTo = null;
	let exitTimer = 0;

	// The card's right edge is the Menu button's, at any width, and its top is the
	// header's bottom edge as measured: with the admin bar showing the sticky
	// header stops below it, so the height alone would put the card under the bar.
	const placeCard = () => {
		const right = root.clientWidth - toggle.getBoundingClientRect().right;
		root.style.setProperty('--menu-right', `${Math.round(right)}px`);
		root.style.setProperty('--header-bottom', `${Math.round(header.getBoundingClientRect().bottom)}px`);
	};

	const setOpen = (next, { restoreFocus = true } = {}) => {
		if (next === open) return;
		open = next;
		window.clearTimeout(exitTimer);
		toggle.setAttribute('aria-expanded', String(next));

		if (next) {
			returnTo = document.activeElement;
			placeCard();
			drawer.hidden = false;
			scrim.hidden = false;
			// Two frames, so the browser paints the closed state before the
			// transition to the open one starts.
			requestAnimationFrame(() => requestAnimationFrame(() => {
				if (!open) return;
				drawer.classList.add('is-in');
				scrim.classList.add('is-in');
			}));
			// The phone sheet covers the page; the desktop card leaves it usable.
			if (mobile.matches) setScrollLock(drawer, true);
			document.body.dataset.dialogOpen = '1';
			const first = focusables(drawer)[0];
			(first || drawer).focus();
		} else {
			drawer.classList.remove('is-in');
			scrim.classList.remove('is-in');
			setScrollLock(drawer, false);
			delete document.body.dataset.dialogOpen;
			exitTimer = window.setTimeout(() => {
				drawer.hidden = true;
				scrim.hidden = true;
			}, EXIT_MS);
			if (restoreFocus && returnTo && document.contains(returnTo)) returnTo.focus();
			returnTo = null;
		}
	};

	toggle.addEventListener('click', () => setOpen(!open));
	if (closeBtn) closeBtn.addEventListener('click', () => setOpen(false));
	scrim.addEventListener('mousedown', () => setOpen(false));
	drawer.querySelectorAll('a[href]').forEach((a) => a.addEventListener('click', () => setOpen(false, { restoreFocus: false })));
	drawer.addEventListener('keydown', (e) => {
		if (e.key === 'Escape') {
			e.stopPropagation();
			setOpen(false);
			return;
		}
		trapFocus(drawer, e);
	});
	// Escape with focus outside the panel (on the toggle, say) closes it too.
	document.addEventListener('keydown', (e) => {
		if (e.key === 'Escape' && open) setOpen(false);
	});

	window.addEventListener('resize', () => {
		if (open) placeCard();
	});
	// The desktop card leaves the page scrollable, and near the top the header
	// has not stuck yet, so its bottom edge moves with the scroll.
	window.addEventListener('scroll', () => {
		if (open) placeCard();
	}, { passive: true });
	// The two layouts lock scroll differently; crossing between them while open
	// would leave the lock in the wrong state, so close instead.
	mobile.addEventListener('change', () => {
		if (open) setOpen(false, { restoreFocus: false });
	});

	// dialog.js closes the panel before opening a dialog from one of its rows.
	window.awddCloseMenu = () => setOpen(false, { restoreFocus: false });
}
